import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Icon, CardV2, Button, ButtonIcon, Feedback } from 'pepsico-ds';
import { urlConstants } from '../../constants/urlConstants';
import { LAST_REFRESHED_LABEL_MAP } from './chatConstants';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import {
  Bar as ChartBar,
  Line as ChartLine,
  Pie as ChartPie,
  Bubble as ChartBubble,
} from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  ChartTooltip,
  ChartLegend,
  ChartDataLabels,
);

const { genAITermsUrl, privacyNoticeUrl } = urlConstants;

const KM_BADGE_STYLE = {
  borderRadius: '9999px',
  borderColor: '#8947E6',
  background: 'linear-gradient(150deg, #591EAD 13.45%, #AD80E4 85.65%)',
};

const CHATBOT_CONFIG = {
  mi: {
    badge: 'MI',
    label: 'GENERATED BY MARKET INTELLIGENCE ASSISTANT',
    placeholder: 'Send a message to Market Intelligence AI Assistant.',
    thinkingName: 'Market Intelligence',
    aiBadgeStyle: {},
    userBadgeStyle: {},
    labelStyle: {  },
  },
  km: {
    badge: 'KM',
    label: 'GENERATED BY KNOWLEDGE MANAGEMENT ASSISTANT',
    placeholder: 'Send a message to Knowledge Management AI Assistant.',
    thinkingName: 'Knowledge Management',
    aiBadgeStyle: KM_BADGE_STYLE,
    userBadgeStyle: { marginLeft: '6px', border: 'none', ...KM_BADGE_STYLE },
    labelStyle: { color: '#5C4EC9' },
  },
};

// ── THINKING ANIMATION ─────────────────────────────────────────────────────

const ThinkingAnimation = ({ queryIntentDone, plannerSteps, elapsedSeconds, chatbot }) => {
  const { badge, aiBadgeStyle, thinkingName } = CHATBOT_CONFIG[chatbot] || CHATBOT_CONFIG.mi;
  const allSteps = [
    { label: 'Analyzing query intent', done: queryIntentDone },
    ...plannerSteps.map((s) => ({ label: s.description, done: s.status === 'complete' })),
    { label: 'Generating insights', done: false },
  ];
  const firstActiveIdx = allSteps.findIndex((s) => !s.done);

  return (
    <div className="mi-msg mi-msg--ai">
      <div className="mi-msg-ai-header">
        <div className="mi-msg-ai-badge" style={aiBadgeStyle}>{badge}</div>
        <div className="mi-thinking-content">
          <div className="mi-thinking-title">
            <Icon icon="timer" />
            <span>Thought for {elapsedSeconds}s</span>
          </div>
          <p className="mi-thinking-description">
            <strong>{thinkingName}</strong> is currently processing your request. This may take a few moments as it analyzes the information and generates the best possible result. Please hold on while we complete this step.
          </p>
          <div className="mi-thinking-steps">
            {allSteps.map((step, idx) => (
              <div
                key={idx}
                className={`mi-thinking-step ${step.done ? 'mi-thinking-step--done' : idx === firstActiveIdx ? 'mi-thinking-step--active' : ''}`}
              >
                <span className="mi-thinking-step-icon">
                  {step.done ? (
                    <Icon icon="check_circle" />
                  ) : idx === firstActiveIdx ? (
                    <Icon icon="play_circle" />
                  ) : (
                    <span className="mi-thinking-step-circle" />
                  )}
                </span>
                <span className="mi-thinking-step-text">{step.label}</span>
              </div>
            ))}
          </div>
          <div className="mi-thinking-dots">
            <span className="mi-dot"></span>
            <span className="mi-dot"></span>
            <span className="mi-dot"></span>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── TABLE HELPERS ──────────────────────────────────────────────────────────

const parseMarkdownTable = (block) => {
  const trimmed = block.trim();
  // A valid markdown table must contain a separator row (e.g. | --- | --- |)
  if (!trimmed.split('\n').some((l) => l.match(/^\s*\|[-:\s|]+\|\s*$/))) {
    return null;
  }
  const lines = trimmed.split('\n')
    .filter((l) => l.trim().startsWith('|') && l.trim().endsWith('|'))
    .filter((l) => !l.match(/^\s*\|[-:\s|]+\|\s*$/));
  if (lines.length < 2) {
    return null;
  }
  const parseRow = (line) => {
    const placeholder = '\uE000';
    const normalized = line.replace(/\\\|/g, placeholder);
    return normalized
      .split('|')
      .map((c) => c.trim().replace(/\uE000/g, '|'))
      .filter((c) => c);
  };
  return {
    rows: lines.slice(1).map(parseRow),
    headers: parseRow(lines[0]),
  };
};

const MONTH_NAME_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
};

// Attempts to parse a cell value as a date, supporting the common formats this
// app renders (MM/YYYY, YYYY-MM, YYYY-MM-DD, "Mon YYYY", MM/DD/YYYY, DD/MM/YYYY).
// Returns a comparable timestamp (ms) or null when the value isn't a recognizable date.
const parseDateValue = (value) => {
  const str = (value || '').trim();
  if (!str) {
    return null;
  }

  let match = str.match(/^(\d{1,2})[/-](\d{4})$/);
  if (match) {
    const month = parseInt(match[1], 10) - 1;
    const year = parseInt(match[2], 10);
    return month >= 0 && month <= 11 ? new Date(year, month, 1).getTime() : null;
  }

  match = str.match(/^(\d{4})[/-](\d{1,2})$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    return month >= 0 && month <= 11 ? new Date(year, month, 1).getTime() : null;
  }

  match = str.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);
    return new Date(year, month, day).getTime();
  }

  match = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (match) {
    const first = parseInt(match[1], 10);
    const second = parseInt(match[2], 10);
    const year = parseInt(match[3], 10);
    // Disambiguate MM/DD/YYYY vs DD/MM/YYYY: whichever part can't be a month must be the day.
    const month = first <= 12 ? first - 1 : second - 1;
    const day = first <= 12 ? second : first;
    return new Date(year, month, day).getTime();
  }

  match = str.match(/^([A-Za-z]{3,9})[\s,]+(\d{4})$/);
  if (match) {
    const monthKey = match[1].slice(0, 3).toLowerCase();
    if (monthKey in MONTH_NAME_INDEX) {
      return new Date(parseInt(match[2], 10), MONTH_NAME_INDEX[monthKey], 1).getTime();
    }
  }

  return null;
};

// Renders inline markdown: **bold**, *italic*, `code` inside table cells
/* istanbul ignore next */
const renderInlineMd = (text) => {
  if (!text) return null;
  const parts = text.split(/([*`][^*`]+[*`]|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (part.startsWith('*') && part.endsWith('*')) return <em key={i}>{part.slice(1, -1)}</em>;
    if (part.startsWith('`') && part.endsWith('`')) return <code key={i}>{part.slice(1, -1)}</code>;
    return part;
  });
};

const SortIcon = ({ dir }) => (
  <svg
    width="16"
    style={{ verticalAlign: 'middle', marginLeft: '4px', flexShrink: 0 }}
    aria-hidden="true"
    height="16"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
  >
    <path
      fill={dir === 'asc' ? '#004895' : '#6B7280'}
      d="M12 5.83L15.17 9l1.41-1.41L12 3 7.41 7.59 8.83 9 12 5.83z"
    />
    <path
      fill={dir === 'desc' ? '#004895' : '#6B7280'}
      d="M12 18.17L8.83 15l-1.41 1.41L12 21l4.59-4.59L15.17 15 12 18.17z"
    />
  </svg>
);

const SortableTable = ({ headers, rows }) => {
  const [sortDir, setSortDir] = React.useState('asc');
  const [sortCol, setSortCol] = React.useState(null);

  const handleSort = (colIdx) => {
    if (sortCol === colIdx) {

      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortDir('asc');
      setSortCol(colIdx);
    }
  };

  const sortedRows = React.useMemo(() => {
    if (sortCol === null) {
      return rows;
    }

    // If every non-empty value in this column parses as a date (e.g. "06/2026",
    // "2026-06", "Jun 2026"), sort chronologically instead of as a stripped number/string.
    const columnValues = rows.map((row) => row[sortCol]).filter(Boolean);
    const isDateColumn = columnValues.length > 0 && columnValues.every((v) => parseDateValue(v) !== null);

    return [...rows].sort((a, b) => {
      const aRaw = a[sortCol] || '';
      const bRaw = b[sortCol] || '';

      if (isDateColumn) {
        const cmp = parseDateValue(aRaw) - parseDateValue(bRaw);
        return sortDir === 'asc' ? cmp : -cmp;
      }

      const bv = bRaw.replace(/[^0-9.-]/g, '');
      const av = aRaw.replace(/[^0-9.-]/g, '');
      const bn = parseFloat(bv);
      const an = parseFloat(av);
      const cmp = !isNaN(an) && !isNaN(bn) ?
            an - bn :
            aRaw.localeCompare(bRaw);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, 
    sortCol, 
    sortDir]);

  const thStyle = {
    whiteSpace: 'nowrap',
    borderTop: '2px solid #D8E7FB',
    borderBottom: '2px solid #D8E7FB',
    textTransform: 'uppercase',
    cursor: 'pointer',
    background: '#F5F9FF',
    letterSpacing: '0.4px',
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '12px',
    color: '#2D2D2D',
    fontWeight: 700,
  };
  const tdStyle = {
    color: '#2D2D2D',
    borderBottom: '2px solid #D8E7FB',
    fontSize: '13px',
    padding: '12px',
    background: '#FFF',
  };

  return (
    <table style={{ borderCollapse: 'collapse', width: '100%', tableLayout: 'auto' }}>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <th key={i} style={thStyle} onClick={() => handleSort(i)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleSort(i); }} tabIndex={0} scope="col">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                {renderInlineMd(h)}
                <SortIcon dir={sortCol === i ? sortDir : null} />
              </span>
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sortedRows.map((row, ri) => (
          <tr key={ri}>
            {row.map((cell, ci) => (
              <td style={tdStyle} key={ci}>{renderInlineMd(cell)}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

// ── CHART HELPERS ──────────────────────────────────────────────────────────

const formatYTick = (value) => {
  const abs = Math.abs(value);
  if (abs >= 1e9) {
    return `$${(value / 1e9).toFixed(1)}B`;
  }
  if (abs >= 1e6) {
    return `$${(value / 1e6).toFixed(1)}M`;
  }
  if (abs >= 1e3) {
    return `$${(value / 1e3).toFixed(0)}K`;
  }
  return String(value);
};

const getText = (value) => {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return '';
  }
  return String(value);
};

const toNumber = (value) => {
  if (typeof value === 'number') {
    return value;
  }
  const parsed = parseFloat(String(value ?? '').replace(/,/g, ''));
  return Number.isNaN(parsed) ? 0 : parsed;
};


const getTitleText = (axisTitle) => {
  if (!axisTitle) {
    return '';
  }
  if (typeof axisTitle === 'string') {
    return axisTitle;
  }
  return axisTitle.text || '';
};

const normalizeChartType = (chartConfig, traces, layout) => {
  const rawType = (chartConfig?.chart_type || '').toLowerCase();
  if (rawType.includes('stack')) {
    return 'stacked_bar';
  }
  if (rawType.includes('line')) {
    return 'line';
  }
  if (rawType.includes('bubble')) {
    return 'bubble';
  }
  if (rawType.includes('pie') || rawType.includes('donut')) {
    return 'pie';
  }
  if (rawType.includes('bar') && layout?.barmode === 'stack') {
    return 'stacked_bar';
  }
  if (rawType.includes('bar')) {
    return 'bar';
  }

  const traceType = (traces?.[0]?.type || '').toLowerCase();
  if (traceType === 'pie') return 'pie';
  if (traceType === 'scatter') {
    return Array.isArray(traces?.[0]?.marker?.size) ? 'bubble' : 'line';
  }
  if (traceType === 'bar' && layout?.barmode === 'stack') return 'stacked_bar';
  return 'bar';
};

// const BAR_COLORS = ['#3380FF', '#8FBFFF', '#5AA3F5', '#0052CC', '#66B2FF'];
// Each added color differs from the others and from the original 5 in both hue AND
// lightness/saturation (not just hue rotation at a fixed tint), since colors that only
// differ by hue at the same pastel tint read as near-identical to the eye.
const BAR_COLORS = [
  '#F4A4C9', '#B7B8FB', '#52D9C6', '#DFBE0E', '#FE937A',
  '#1E88E5', '#43A047', '#8E24AA', '#F4511E', '#00695C',
  '#D81B60', '#3949AB', '#6D4C41', '#546E7A', '#9E9D24',
];

/* istanbul ignore next */
const maybeQualifyForecast = (seriesName, valueText) => {
  if (/forecast|fcst/i.test(seriesName || '') && !/^FCST:/i.test(valueText || '')) {
    return `FCST: ${valueText}`;
  }
  return valueText;
};

/* istanbul ignore next */
const getOrCreateTooltipElement = (chart) => {
  const parent = chart.canvas.parentNode;
  let tooltipEl = parent.querySelector('.sa-conv-chart-external-tooltip');
  if (!tooltipEl) {
    tooltipEl = document.createElement('div');
    tooltipEl.className = 'sa-conv-chart-external-tooltip';
    tooltipEl.style.position = 'absolute';
    tooltipEl.style.pointerEvents = 'none';
    tooltipEl.style.opacity = 0;

    parent.appendChild(tooltipEl);
  }
  return tooltipEl;
};

/* istanbul ignore next */
const formatTooltipValue = (chartType, point) => {
  const seriesName = point?.dataset?.label || 'Value';
  if (chartType === 'bubble') {
    const raw = point?.raw || {};
    const valueText = raw.valueLabel || formatYTick(raw.y || 0);
    return { seriesName, valueText: maybeQualifyForecast(seriesName, valueText) };
  }
  const idx = point?.dataIndex ?? 0;
  const textLabels = point?.dataset?.textLabels || [];
  const parsedValue = chartType === 'pie' ? point?.parsed : point?.parsed?.y;
  if (parsedValue === null || parsedValue === undefined) {
    return null;
  }
  const fallback = formatYTick(parsedValue);
  const valueText = textLabels[idx] || fallback;
  return { seriesName, valueText: maybeQualifyForecast(seriesName, valueText) };
};

/* istanbul ignore next */
const externalTooltipRenderer = (chartType) => (context) => {
  const { chart, tooltip } = context;

  const tooltipEl = getOrCreateTooltipElement(chart);

  if (!tooltip || tooltip.opacity === 0) {
    tooltipEl.style.opacity = 0;
    return;
  }
  const title =
    tooltip.title?.[0] || tooltip.dataPoints?.[0]?.label ||
    tooltip.dataPoints?.[0]?.raw?.xLabel || '';
  const rowsHtml = (tooltip.dataPoints || [])
    .map((point) => formatTooltipValue(chartType, point))
    .filter(Boolean)
    .map(({ seriesName, valueText }) => `<div class="sa-conv-chart-external-tooltip-row"><span class="sa-conv-chart-external-tooltip-name">${seriesName}:</span><span class="sa-conv-chart-external-tooltip-value"> ${valueText}</span></div>`)
    .join('');
  if (!rowsHtml) {
    tooltipEl.style.opacity = 0;
    return;
  }
  tooltipEl.innerHTML = `
    ${title ? `<div class="sa-conv-chart-external-tooltip-label">${title}</div>` : ''}
    ${rowsHtml}
  `;
  const parentRect = chart.canvas.parentNode.getBoundingClientRect();
  let left = tooltip.caretX + 12;
  let top = tooltip.caretY + 12;
  tooltipEl.style.opacity = 1;
  const tipWidth = tooltipEl.offsetWidth;
  const tipHeight = tooltipEl.offsetHeight;
  left = Math.max(8, Math.min(left, parentRect.width - tipWidth - 8));
  top = Math.max(8, Math.min(top, parentRect.height - tipHeight - 8));
  tooltipEl.style.left = `${left}px`;
  tooltipEl.style.top = `${top}px`;
};

const MIChart = ({ chartConfig }) => {
  const wrapperRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(900);

  useEffect(() => {
    if (!wrapperRef.current || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) => {

      const nextWidth = entries?.[0]?.contentRect?.width;
      if (nextWidth) {
        setChartWidth(nextWidth);
      }
    });
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, []);

  const layout = chartConfig?.config?.layout || {};
  const traces = chartConfig?.config?.data || [];
  if (!traces.length) {
    return null;
  }

  const title = layout?.title?.text || '';
  const chartType = normalizeChartType(chartConfig, traces, layout);
  const yAxisLabel = getTitleText(layout?.yaxis?.title);
  const xAxisLabel = getTitleText(layout?.xaxis?.title);
  const yTickFormat = layout?.yaxis?.tickformat || null;
  const legendTitleText = layout?.legend?.title?.text || null;
  const legendOrientation = layout?.legend?.orientation || 'v'; // 'v' = vertical, 'h' = horizontal
  const xTickAngle = layout?.xaxis?.tickangle != null ? Number(layout.xaxis.tickangle) : null;

  const isSmall = chartWidth < 640;
  const isCompact = chartWidth < 860;

  // Build the category/x-axis label set from ALL traces (not just traces[0]) - each
  // trace may carry a different subset of x categories (e.g. one point per trace),
  // so relying on a single trace's x array would drop categories only present elsewhere.
  const labels = [];
  const seenLabels = new Set();
  traces.forEach((trace) => {
    (trace.x || []).forEach((xVal) => {
      const text = getText(xVal);
      if (!seenLabels.has(text)) {
        seenLabels.add(text);
        labels.push(text);
      }
    });
  });
  const hasLongXLabels = labels.some((l) => l.length > 20);

  // Build a y-axis tick formatter: use layout tickformat when provided, else fall back to K/M/B shorthand
  const makeYTickCallback = () => {
    if (yTickFormat) {
      return (value) => {
        // Support common numeric formats: ,.2f → comma-separated with 2 decimals
        const decimals = (yTickFormat.match(/[.](\d{1,2})f/) || [])[1];
        if (decimals !== undefined) {
          return Number(value).toLocaleString(undefined, {
            minimumFractionDigits: Number(decimals),
            maximumFractionDigits: Number(decimals),
          });
        }
        return formatYTick(value);
      };
    }
    return (value) => formatYTick(value);
  };

  const toNullableNumber = (value) =>
    value === null || value === undefined ? null : toNumber(value);

  const cartesianDatasets = traces.map((trace, idx) => {
    // Use marker.size from config for line/scatter; fall back to responsive defaults
    const markerSize = typeof trace?.marker?.size === 'number' ? trace.marker.size : null;
    const pointR = markerSize ?? (isSmall ? 2 : 3);
    const pointHoverR = markerSize ? markerSize + 2 : (isSmall ? 4 : 5);
    // Map this trace's own x values to their index so values land on the right
    // category slot in the merged `labels` set, instead of assuming every trace
    // shares the same x array / ordering.
    const xToIndex = new Map((trace.x || []).map((xVal, i) => [getText(xVal), i]));
    return {
      borderColor: BAR_COLORS[idx % BAR_COLORS.length],
      label: trace.name || `series_${idx + 1}`,
      textLabels: labels.map((label) => {
        const i = xToIndex.get(label);
        return i === undefined ? '' : getText(trace.text?.[i]);
      }),
      borderWidth: 2,
      // Keep nulls as null (instead of coercing to 0) so Chart.js skips rendering
      // bars/points, data labels, and tooltips for missing values.
      data: labels.map((label) => {
        const i = xToIndex.get(label);
        return i === undefined ? null : toNullableNumber(trace.y?.[i]);
      }),
      tension: 0.35,
      spanGaps: false,
      backgroundColor: BAR_COLORS[idx % BAR_COLORS.length],
      pointHoverRadius: pointHoverR,
      pointRadius: pointR,
    };
  });

  const bubbleDatasets = traces.map((trace, tIdx) => {
    const sizes = trace?.marker?.size || [];
    return {
      label: trace.name || `series_${tIdx + 1}`,
      data: (trace.x || []).map((xVal, idx) => {
        const yVal = toNumber(trace?.y?.[idx]);
        return {
          x: idx + 1,
          xLabel: getText(xVal),
          y: yVal,
          z: Math.max(6, toNumber(sizes?.[idx] ?? sizes ?? 14)),
          valueLabel: getText(trace?.text?.[idx]) || formatYTick(yVal),
        };
      }),
      borderColor: BAR_COLORS[tIdx % BAR_COLORS.length],
      backgroundColor: `${BAR_COLORS[tIdx % BAR_COLORS.length]}88`,
      pointRadius: (ctx) => ctx.raw?.z || 8,
      pointHoverRadius: (ctx) => (ctx.raw?.z || 8) + 2,
    };
  });
  const bubblePointCount = bubbleDatasets.reduce((acc, s) => acc + s.data.length, 0);

  const pieTrace = traces[0] || {};
  const pieLabels = (pieTrace.labels || []).map(getText);
  const pieValues = (pieTrace.values || []).map(toNumber);
  const pieText = (pieTrace.text || []).map(getText);

  const dataCount =
    chartType === 'pie' ? pieLabels.length : chartType === 'bubble' ? bubblePointCount : labels.length;
  const maxTicks = Math.max(2, Math.floor((chartWidth - 140) / (isSmall ? 70 : 90)));
  const rotateXTicks = (xTickAngle != null ? Math.abs(xTickAngle) > 0 : false) || dataCount > 7 || isCompact;
  const effectiveTickAngle = xTickAngle != null ? Math.abs(xTickAngle) : (rotateXTicks ? 35 : 0);
  const showDataLabels = !isCompact && dataCount <= 10;
  // Increase chart height when rotated long labels would otherwise overlap the x-axis title
  const chartHeight = isSmall ? 320 : (rotateXTicks && hasLongXLabels ? 500 : 420);
  const MAX_LABEL_LEN = 22;

  // Legend position: if orientation='h' use bottom, else use top (unless pie)
  const legendPosition = chartType === 'pie' ? 'bottom' : (legendOrientation === 'h' ? 'bottom' : 'top');

  const commonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    devicePixelRatio: window.devicePixelRatio || 1,
    interaction: { mode: 'nearest', intersect: false },
    scales: {
      x: {
        stacked: chartType === 'stacked_bar',
        ticks: {
          font: { size: isSmall ? 11 : 14, family: 'Inter' },
          color: '#616161',
          minRotation: effectiveTickAngle,
          maxRotation: effectiveTickAngle,
          autoSkip: true,
          maxTicksLimit: maxTicks,
          // Truncate long labels so rotated ticks don't overlap the x-axis title
          callback: (value, index) => {
            const label = labels[index] !== undefined ? String(labels[index]) : String(value);
            return label.length > MAX_LABEL_LEN ? `${label.slice(0, MAX_LABEL_LEN - 1)}…` : label;
          },
        },
        grid: { display: false },
        title: xAxisLabel
          ? { display: true, text: xAxisLabel, color: '#616161', font: { size: isSmall ? 11 : 14, family: 'Inter' } }
          : { display: false },
      },
      y: {
        stacked: chartType === 'stacked_bar',
        ticks: { callback: makeYTickCallback(), color: '#616161', font: { size: isSmall ? 11 : 14, family: 'Inter' } },
        grid: { color: '#F0F0F0', borderDash: [3, 3] },
        title: yAxisLabel
          ? { display: true, text: yAxisLabel, color: '#616161', font: { size: isSmall ? 11 : 14, family: 'Inter' } }
          : { display: false },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: legendPosition,
        title: legendTitleText ? { display: true, text: legendTitleText, color: '#616161', font: { size: isSmall ? 11 : 14, family: 'Inter', weight: 'bold' } } : { display: false },
        labels: { boxWidth: 12, color: '#616161', font: { size: isSmall ? 11 : 14, family: 'Inter' } },
      },
      tooltip: { enabled: false, external: externalTooltipRenderer(chartType) },
      datalabels: {
        display: (chartType === 'bar' || chartType === 'stacked_bar') ? showDataLabels : false,
        anchor: 'end',
        align: 'top',
        color: '#616161',
        font: { size: 11, family: 'Inter', weight: 'normal' },
        formatter: (value, ctx) => {
          if (value === null || value === undefined) {
            return null;
          }
          const labelsArr = ctx.dataset?.textLabels || [];
          return labelsArr[ctx.dataIndex] || formatYTick(value);
        },
      },
    },
  };

  const barData = {
    labels,
    datasets: cartesianDatasets.map((ds) => ({
      ...ds,
      borderRadius: chartType === 'stacked_bar' ? 0 : 3,
      maxBarThickness: isSmall ? 36 : 56,
      // By default Chart.js reserves a bar "slot" per category for every dataset in the
      // chart - even when a dataset's value is null for that category - which leaves a
      // visible gap in a grouped (non-stacked) bar chart. skipNull tells Chart.js to
      // compute the slot count per-category from only the datasets that actually have a
      // value there, so bars compact together instead of leaving empty space.
      skipNull: chartType === 'bar',
    })),
  };
  const lineData = {
    labels,
    datasets: cartesianDatasets.map((ds) => ({ ...ds, fill: false })),
  };
  const pieData = {
    labels: pieLabels,
    datasets: [{
      label: pieTrace.name || 'Value',
      data: pieValues,
      textLabels: pieText,
      backgroundColor: pieLabels.map((_, idx) => BAR_COLORS[idx % BAR_COLORS.length]),
      borderColor: '#FFFFFF',
      borderWidth: 1,
    }],
  };
  const bubbleData = { datasets: bubbleDatasets };
  const bubbleOptions = {
    ...commonOptions,
    scales: {
      x: {
        ...commonOptions.scales.x,
        type: 'linear',
        ticks: {
          ...commonOptions.scales.x.ticks,
          callback: (value) => labels[Math.max(0, Math.round(value) - 1)] || '',
        },
      },
      y: { ...commonOptions.scales.y },
    },
    plugins: { ...commonOptions.plugins, datalabels: { display: false } },
  };
  const pieOptions = {
    ...commonOptions,
    scales: undefined,
    plugins: {
      ...commonOptions.plugins,
      datalabels: {
        display: showDataLabels && pieLabels.length <= 8,
        color: '#616161',
        font: { size: 11, family: 'Inter' },
        formatter: (_value, ctx) => pieLabels[ctx.dataIndex] || '',
      },
    },
  };

  return (
    <div className="sa-conv-chart-wrapper" ref={wrapperRef}>
      {title && <div className="sa-conv-chart-title">{title}</div>}
      <div className="sa-conv-chart-canvas" style={{ height: `${chartHeight}px` }}>
        {chartType === 'pie' && <ChartPie data={pieData} options={pieOptions} />}
        {chartType === 'line' && (
          <ChartLine
            data={lineData}
            options={{ ...commonOptions, plugins: { ...commonOptions.plugins, datalabels: { display: false } } }}
          />
        )}
        {chartType === 'bubble' && <ChartBubble data={bubbleData} options={bubbleOptions} />}
        {(chartType === 'bar' || chartType === 'stacked_bar') && <ChartBar data={barData} options={commonOptions} />}
      </div>
    </div>
  );
};

// ── RESPONSE RENDERER (markdown + sortable tables) ─────────────────────────

const getLastLine = (text) => {
  if (!text) return '';
  const idx = text.lastIndexOf('\n');
  return idx >= 0 ? text.slice(idx + 1) : text;
};

const renderMd = (markdown) => {
  if (!markdown) return null;
  return <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>;
};

const isSeparatorLine = (line) => {
  const t = (line || '').trim();
  if (!t.startsWith('|') || !t.endsWith('|')) return false;
  const cells = t.slice(1, -1).split('|');
  return cells.length > 0 && cells.every((c) => /^[ :-]+$/.test(c));
};

const isTableRow = (line) => {
  const t = (line || '').trim();
  return t.startsWith('|') && t.endsWith('|');
};

const renderResponse = (markdown) => {
  if (!markdown) return null;

  const lines = markdown.split('\n');
  const tableRanges = [];
  let i = 0;
  while (i < lines.length) {
    if (isSeparatorLine(lines[i]) && i > 0 && isTableRow(lines[i - 1])) {
      const start = i - 1;
      let end = i;
      let j = i + 1;
      while (j < lines.length && isTableRow(lines[j]) && !isSeparatorLine(lines[j])) {
        end = j;
        j++;
      }
      tableRanges.push({ start, end });
      i = end + 1;
    } else {
      i++;
    }
  }

  if (tableRanges.length === 0) {
    return <div className="mi-conv-response-text">{renderMd(markdown)}</div>;
  }

  const parts = [];
  let key = 0;
  let lastEnd = 0;

  for (const range of tableRanges) {
    const before = lines.slice(lastEnd, range.start).join('\n').trim();
    if (before) {
      parts.push(<div key={key++} className="mi-conv-response-text">{renderMd(before)}</div>);
    }

    const tableBlock = lines.slice(range.start, range.end + 1).join('\n');
    const parsed = parseMarkdownTable(tableBlock);
    if (parsed) {
      const tableTitle = getLastLine(lines.slice(0, range.start).join('\n')).replace(/\*\*/g, '').trim();
      parts.push(
        <div key={key++} style={{ marginLeft: '48px', marginTop: '12px', marginBottom: '12px' }}>
          <CardV2
            state="non-interactive"
            type="basic"
            nested={true}
            cardHeader={{ title: tableTitle || 'Details', type: 'default' }}
            cardBody={{
              type: 'slot',
              slot: (
                <div style={{ width: '100%', overflowX: 'auto', overflowY: 'auto', maxHeight: '300px' }} className="sa-conv-card-table-slot">
                  <SortableTable headers={parsed.headers} rows={parsed.rows} />
                </div>
              ),
            }}
            viewport="desktop"
            style={{ borderColor: '#D8E7FB' }}
          />
        </div>,
      );
    } else {
      parts.push(<div key={key++} className="mi-conv-response-text">{renderMd(tableBlock)}</div>);
    }

    lastEnd = range.end + 1;
  }

  const after = lines.slice(lastEnd).join('\n').trim();
  if (after) {
    parts.push(<div key={key++} className="mi-conv-response-text">{renderMd(after)}</div>);
  }

  return parts.length > 0 ? parts : <div className="mi-conv-response-text">{renderMd(markdown)}</div>;
};

const formatHeader = (key) =>
  key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');

// ── AI MESSAGE ─────────────────────────────────────────────────────────────

const AIMessage = ({
  msg,
  onFollowUp,
  onShowSources,
  chatbot,
  feedback,
  onFeedback,
  messageIndex,
  feedbackFormOpen,
  feedbackText,
  feedbackError,
  feedbackSubmitPending,
  feedbackAlreadySubmitted,
  onFeedbackTextChange,
  onFeedbackCancel,
  onFeedbackSubmit,
}) => {
  const sourceQueries = Array.isArray(msg.sqlQueries) ? msg.sqlQueries : [];
  const sourceCitations = Array.isArray(msg.citations) ? msg.citations : [];
  const sourceCount = sourceQueries.length + sourceCitations.length || msg.sourcesCount || 0;
  const visibleBadgeCount = Math.min(sourceCount, 2);
  const { badge, label, aiBadgeStyle, labelStyle } = CHATBOT_CONFIG[chatbot] || CHATBOT_CONFIG.mi;
  const isNegativeFeedback = feedback === 'down';
  const feedbackFormIcon = isNegativeFeedback ? 'thumb_down' : 'thumb_up';
  const feedbackFormTitle = isNegativeFeedback ? 'What went wrong?' : 'What did you like?';
  const feedbackFormSubtitle = isNegativeFeedback
    ? 'Let us know what happened and how we can improve:'
    : 'Tell us what worked well for you';
  const feedbackPlaceholder = isNegativeFeedback
    ? 'Describe the issue you experienced...'
    : 'Share what stood out to you...';
  const disableFeedbackActions = feedbackSubmitPending;
  const disableThumbUp = feedbackSubmitPending || (feedbackAlreadySubmitted && feedback !== 'up');
  const disableThumbDown = feedbackSubmitPending || (feedbackAlreadySubmitted && feedback !== 'down');

  return (
    <div className="mi-msg mi-msg--ai">
      <div className="mi-msg-ai-header">
        <div className="mi-msg-ai-badge" style={aiBadgeStyle}>{badge}</div>
        <div className="mi-msg-ai-meta">
          {!msg.isError && <span className="mi-msg-ai-label" style={labelStyle}>{label}</span>}
          {!msg.isError && (
            <span className="mi-msg-ai-date">
              {msg.lastRefreshed && Object.keys(msg.lastRefreshed).length > 0
                ? Object.entries(msg.lastRefreshed)
                    .map(([key, date]) => `Data as on for ${LAST_REFRESHED_LABEL_MAP[key] || key}: ${date}`)
                    .join(' | ')
                : 'Data As On: -'}
            </span>
          )}
          {msg.isError && (
            <>
            <div className="mi-conv-response-text" style={{display: 'flex', alignItems: 'center', gap: '6px', margin: 0 }}>
              <Icon icon="running_with_errors" style={{ color: '#AB2B09', fontSize: '18px' }} />
              {msg.response}
            </div>
            <div>We're unable to process your request at this time. Please try again.</div>
            </>
          )}
        </div>
      </div>

      {msg.insight && (
        <div className="mi-conv-insight">
          <div className="mi-conv-insight-label">
            <span className="mi-conv-insight-icon" aria-hidden="true">
              <Icon icon="info" style={{ color: '#006FCF' }} />
            </span>
            <span style={{ fontWeight:700 }}>Insight</span>
          </div>
          <p style={{marginLeft: '1.8rem'}}>{renderMd(msg.insight)}</p>
        </div>
      )}


      {msg.response && !msg.isError && (
        <div className="mi-msg-ai-content">
          {renderResponse(msg.response)}
        </div>
      )}

      {msg.chartConfig && <MIChart chartConfig={msg.chartConfig} />}

      {msg.genieRawData && msg.genieRawData.length > 0 && (() => {
        const keys = Object.keys(msg.genieRawData[0]);
        const headers = keys.map(formatHeader);
        const rows = msg.genieRawData.map((row) => keys.map((k) => row[k] != null ? String(row[k]) : ''));
        return (
          <div style={{ width: '100%', boxSizing: 'border-box', paddingLeft: '48px', marginTop: '12px', marginBottom: '12px' }}>
            <CardV2
              state="non-interactive"
              type="basic"
              nested={true}
              cardHeader={{ title: 'Raw Data', type: 'default' }}
              cardBody={{
                type: 'slot',
                slot: (
                  <div style={{ width: '100%', overflowX: 'auto', overflowY: 'auto', maxHeight: '300px' }} className="sa-conv-card-table-slot">
                    <SortableTable headers={headers} rows={rows} />
                  </div>
                ),
              }}
              viewport="desktop"
              style={{ borderColor: '#D8E7FB' }}
            />
          </div>
        );
      })()}

      <div className="sa-conv-feedback-section">
        <div className="sa-conv-actions">
          
          <ButtonIcon
            type="naked"
            size="small"
            active={feedback === 'up'}
            icon="thumb_up"
            label="Helpful"
            disabled={disableThumbUp}
            onClick={() => onFeedback && onFeedback('up')}
          />
          <ButtonIcon
            type="naked"
            size="small"
            active={feedback === 'down'}
            icon="thumb_down"
            label="Not Helpful"
            disabled={disableThumbDown}
            onClick={() => onFeedback && onFeedback('down')}
          />
          {sourceCount > 0 && (
            <>
              
              <div className="sa-conv-actions-divider" /> 
             
              <button
                className="sa-conv-sources-chip"
                onClick={() => onShowSources && onShowSources({ sqlQueries: sourceQueries, citations: sourceCitations })}
                title="View sources"
              >
                <div className="sa-conv-sources-badges">
                  {Array.from({ length: visibleBadgeCount }, (_, i) => i).map((i) => (
                    <div key={i} className="sa-conv-source-badge" style={{ zIndex: visibleBadgeCount - i }}>
                      <div className="sa-conv-source-badge-initial">{String.fromCharCode(65 + i)}</div>
                    </div>
                  ))}
                  {sourceCount > 2 && (
                    <div className="sa-conv-source-badge-count">+{sourceCount - 2}</div>
                  )}
                </div>
                <span className="sa-conv-sources-label">Sources</span>
              </button>
            </>
          )}
        </div>

        {feedbackFormOpen === messageIndex && (
          <CardV2
            state="interactive"
            type="basic"
            nested={true}
            className="sa-conv-feedback-card"
            cardBody={{
              type: 'slot',
              slot: (
                <div className="sa-conv-feedback-content">
                  <div className="sa-conv-feedback-header">
                    <Icon icon={feedbackFormIcon} className="sa-conv-feedback-icon" />
                    <h3 className="sa-conv-feedback-title">{feedbackFormTitle}</h3>
                  </div>
                  <p className="sa-conv-feedback-subtitle">{feedbackFormSubtitle}</p>
                  <textarea
                    className="sa-conv-feedback-textarea"
                    placeholder={feedbackPlaceholder}
                    value={feedbackText}
                    onChange={(e) => onFeedbackTextChange && onFeedbackTextChange(e.target.value)}
                  />
                  {feedbackError && (
                    <p className="sa-conv-feedback-error"><Icon icon="error" />{feedbackError}</p>
                  )}
                  <div className="sa-conv-feedback-info">
                    <Feedback message="Your conversation will be included with your feedback to help improve the AI agent." />
                  </div>
                  <div className="sa-conv-feedback-actions">
                    <Button
                      text="Cancel"
                      size="small"
                      disabled={feedbackSubmitPending}
                      onClick={() => {
                        if (!feedbackAlreadySubmitted) onFeedback && onFeedback(null);
                        onFeedbackCancel && onFeedbackCancel();
                      }}
                    />
                    <Button
                      text={feedbackSubmitPending ? 'Submitting...' : 'Submit'}
                      size="small"
                      disabled={feedbackSubmitPending || feedbackAlreadySubmitted || Boolean(feedbackError) || !feedbackText.trim()}
                      onClick={() => onFeedbackSubmit && onFeedbackSubmit(messageIndex, msg, feedback)}
                    />
                  </div>
                </div>
              ),
            }}
          />
        )}
      </div>

      {msg.followUps && msg.followUps.length > 0 && (
        <div className="mi-msg-followups">
          <div className="mi-msg-followups-title">Follow-ups</div>
          {msg.followUps.map((f, i) => (
            <div key={i} className="mi-msg-followup-link" role="button" tabIndex={0} onClick={() => onFollowUp(f)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onFollowUp(f); }}>
              <Icon icon="keyboard_return" />
              <span style={{ marginLeft: '8px' }}>{f}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────

const MIChatActiveView = ({
  messages,
  isThinking,
  queryIntentDone,
  plannerSteps,
  elapsedSeconds,
  message,
  setMessage,
  handleKeyPress,
  handleSendMessage,
  messagesContainerRef,
  onShowSources,
  messageFeedback,
  feedbackFormOpen,
  feedbackText,
  feedbackError,
  feedbackSubmitPendingByMessage,
  submittedFeedbackByMessage,
  getFeedbackMessageKey,
  onMessageFeedback,
  onFeedbackTextChange,
  onFeedbackCancel,
  onFeedbackSubmit,
  chatbot = 'mi',
}) => {
  const { placeholder, userBadgeStyle } = CHATBOT_CONFIG[chatbot] || CHATBOT_CONFIG.mi;
  const localRef = useRef(null);
  const messagesAreaRef = messagesContainerRef || localRef;

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
    // messagesAreaRef is a stable ref object — its .current changes but the ref itself doesn't
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, isThinking, plannerSteps]);

  return (
    <>
      <div className="mi-messages-area" ref={messagesAreaRef}>
        {messages.map((msg, idx) =>
          msg.type === 'user' ? (
            <div key={idx} className="mi-msg mi-msg--user">
              <div className="mi-msg-bubble mi-msg-bubble--user">{msg.text}</div>
              <div className="mi-msg-user-badge" style={userBadgeStyle}>
                <img
                  src="https://static.vecteezy.com/system/resources/previews/013/360/247/original/default-avatar-photo-icon-social-media-profile-sign-symbol-vector.jpg"
                  alt="User avatar"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
                />
              </div>
            </div>
          ) : (
            <AIMessage
              key={idx}
              msg={msg}
              onFollowUp={(f) => setMessage(f)}
              onShowSources={onShowSources}
              chatbot={chatbot}
              feedback={messageFeedback?.[idx]}
              onFeedback={(feedbackType) => onMessageFeedback && onMessageFeedback(idx, feedbackType, msg)}
              messageIndex={idx}
              feedbackFormOpen={feedbackFormOpen}
              feedbackText={feedbackText}
              feedbackError={feedbackFormOpen === idx ? feedbackError : ''}
              feedbackSubmitPending={Boolean(feedbackSubmitPendingByMessage?.[getFeedbackMessageKey?.(idx, msg)])}
              feedbackAlreadySubmitted={Boolean(submittedFeedbackByMessage?.[getFeedbackMessageKey?.(idx, msg)])}
              onFeedbackTextChange={onFeedbackTextChange}
              onFeedbackCancel={onFeedbackCancel}
              onFeedbackSubmit={onFeedbackSubmit}
            />
          ),
        )}
        {isThinking && (
          <ThinkingAnimation
            queryIntentDone={queryIntentDone}
            plannerSteps={plannerSteps || []}
            elapsedSeconds={elapsedSeconds}
            chatbot={chatbot}
          />
        )}
      </div>

      <div className="mi-chat-input-area mi-chat-input-area--bottom">
        <div className="mi-input-wrapper">
          <input
            type="text"
            className="mi-input"
            placeholder={placeholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <Button
            variant="primary"
            size="medium"
            iconTrailing="send"
            onClick={handleSendMessage}
            disabled={!message.trim()}
            className="mi-send-btn"
          />
        </div>
        <p className="mi-input-disclaimer">
          By messaging the AI assistant, you confirm that you have read and agree to our{' '}
          <a href={genAITermsUrl} target="_blank" rel="noopener noreferrer">Terms & Conditions</a> and <a href={privacyNoticeUrl} target="_blank" rel="noopener noreferrer">Privacy Notice</a>.
          Please do not share sensitive information. Chats may be reviewed and used to improve our models. AI can make mistakes, so please verify important information.
        </p>
      </div>
    </>
  );
};

export default MIChatActiveView;
